import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";
import { ReceiverDetail } from "../models/SendLineMessage";

/**
 * ### 予約送信メッセージ API 操作クラス
 */
export default class ScheduledMessages {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET ScheduledMessages
   */
  public async gets(userStore: LoginUser, queryParams?: ScheduledMessageGetsQuery): Promise<Array<ScheduledMessageGetModel>> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ScheduledMessages, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<ScheduledMessageGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET ScheduledMessages Count
   */
  public async getCount(userStore: LoginUser, queryParams?: ScheduledMessageGetsQuery): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.facility.id
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ScheduledMessages, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(
      userStore,
      `${requestUrl}/count`,
      this.constructor.name,
      queryParams || {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data.count;
  }

  /**
   * PATCH ScheduledMessages
   */
  public async patch(userStore: LoginUser, scheduledMessageId: string, requestBody: ScheduledMessagePatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      scheduledMessageId: scheduledMessageId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ScheduledMessage, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE ScheduledMessages
   */
  public async delete(userStore: LoginUser, scheduledMessageId: string): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.facility.id,
      scheduledMessageId: scheduledMessageId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.ScheduledMessage, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface ScheduledMessageGetsQuery {
  /** 開始インデックス */
  startIndex?: number;
  /** 終了インデックス */
  endIndex?: number;
  /** 開始日時 */
  start?: string;
  /** 終了日時 */
  end?: string;
  /** ステータス ("scheduled" | "sent" | "canceled" | "all") */
  status?: "scheduled" | "sent" | "canceled" | "all" | string;
  /** 送信対象 ("family" | "waiter" | "all") */
  target?: "family" | "waiter" | "all" | string;
}

/**
 * ### 予約送信メッセージ
 */
export interface ScheduledMessageGetModel {
  /** 施設ID */
  facilityId: number;
  /** 予約送信メッセージID (ソートキー) */
  scheduledMessageId: string;
  /** 送信種別 ("families" または "otherUserGroups") */
  type: Type;
  /** 送信予定日時（JST: YYYYMMDDHHMMSS形式） */
  scheduledTime: number;
  /** 送信ステータス ("scheduled" | "sent" | "failed" | "canceled") */
  status: Status;
  /** 送信に必要なパラメータ（JSON文字列） */
  payload: string;
  /** 宛先詳細情報 */
  receiverDetails?: Array<ReceiverDetail>;
  /** EventBridge Schedulerで一意のスケジュール名 */
  scheduleName: string;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 作成日時(YYYYMMDDhhmmssSSS) */
  created: number;
  /** 更新日時(YYYYMMDDhhmmssSSS) */
  updated: number;
  /** エラーメッセージ（送信失敗時のみ格納） */
  errorMessage?: string;
}

/**
 * ### 送信種別
 */
export type Type =
  // 家族全員
  | "allFamilies"
  // 家族個別
  | "families"
  // 施設関係者グループメンバー
  | "otherUserGroupMembers"
  // 宛先グループ（家族向け）
  | "sendMessageGroupsForFamilies"
  // 宛先グループ（施設関係者向け）
  | "sendMessageGroupsForOtherUserGroups"
  // 利用者グループ
  | "residentGroups"
  // 施設関係者グループ
  | "otherUserGroups"
  // 入居申込者全員
  | "allWaiters"
  // 入居申込者個別
  | "waiters";

/**
 * ### 送信ステータス
 */
export type Status = "scheduled" | "sent" | "failed" | "canceled";

/**
 * ### 予約送信メッセージ
 */
export interface ScheduledMessagePatchModel {
  /** 送信予定日時（JST: YYYYMMDDHHMMSS形式） */
  scheduledTime: number;
  /** 送信に必要なパラメータ（JSON文字列、またはオブジェクト） */
  payload: any;
}
